// ─── Auth & Portal utilities ─────────────────────────────────────

import { supabase } from './supabaseClient';

const SESSION_KEY = 'str_auth_v3';
// ─── Office location (GPS-based clock-in) ───────────────────────
export const OFFICE_LOCATION = {
  lat:    31.460459,
  lng:    73.120365,
  radius: 1000,         // metres — allowed clock-in radius
};

// ── Session ──────────────────────────────────────────────────────
// Staff sign in with Supabase Auth: the password is checked by the server and the
// signed-in token is what the database trusts (row-level rules check it). The role
// comes from the account's app_metadata, which only the server can set:
//   { role: 'admin' }  or  { role: 'employee', emp_id: 'emp3' }
// A small copy of { role, empId, username } is kept in localStorage so the many
// synchronous getSession() callers keep working; it is rebuilt from the real Supabase
// session on every start (restoreSession) and cleared on sign-out.
const STAFF_DOMAIN = 'staff.strategistspk.com';
const LOGIN_ALIASES = { shehryarnaveed: 'shehryar@strategistspk.com' };   // admin's username → their account email
const KEEP_KEY = 'str_keep_signed_in';
const TAB_KEY  = 'str_tab_session';

export function loginEmail(username) {
  const u = String(username || '').trim().toLowerCase();
  if (!u) return '';
  if (u.includes('@')) return u;
  return LOGIN_ALIASES[u] || (u + '@' + STAFF_DOMAIN);
}
export function usernameFromEmail(email) {
  const e = String(email || '').toLowerCase();
  const alias = Object.keys(LOGIN_ALIASES).find(k => LOGIN_ALIASES[k] === e);
  if (alias) return alias;
  return e.endsWith('@' + STAFF_DOMAIN) ? e.slice(0, -(STAFF_DOMAIN.length + 1)) : e;
}

function sessionFromUser(user) {
  const meta = (user && user.app_metadata) || {};
  if (meta.role !== 'admin' && meta.role !== 'employee') return null;
  return {
    role: meta.role,
    empId: meta.role === 'employee' ? (meta.emp_id || null) : undefined,
    username: usernameFromEmail(user.email),
    userId: user.id,
  };
}
function writeSession(s) {
  try {
    if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s)); else localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem('str_auth_v2');                  // the old, pre-Auth session
  } catch (e) { /* storage blocked */ }
}

export function getSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function isAuthenticated() { return !!getSession(); }

// Sign in with username (or email) + password. Resolves to the session, or throws an
// Error whose message is safe to show.
export async function signIn(username, password, keep = true) {
  const email = loginEmail(username);
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) {
    if (/invalid login|invalid_credentials|invalid/i.test(error.message || '')) throw new Error('Incorrect username or password.');
    throw new Error('Could not sign in — check your connection and try again.');
  }
  const s = sessionFromUser(data && data.user);
  if (!s) { await supabase.auth.signOut(); throw new Error('This account does not have access to the Strategists app.'); }
  writeSession(s);
  try {
    localStorage.setItem(KEEP_KEY, keep ? '1' : '0');
    sessionStorage.setItem(TAB_KEY, '1');
    if (s.empId) localStorage.setItem('str_emp_id', String(s.empId));   // the /c/ calendar link uses it
  } catch (e) { /* ignore */ }
  return s;
}

// On start: trust only the real Supabase session (the token is refreshed by the
// client). "Keep me signed in" unticked → a new browser session starts signed out.
export async function restoreSession() {
  try {
    if (localStorage.getItem(KEEP_KEY) === '0' && !sessionStorage.getItem(TAB_KEY)) {
      await supabase.auth.signOut({ scope: 'local' });
      writeSession(null);
      return null;
    }
    const { data } = await supabase.auth.getSession();
    const s = sessionFromUser(data && data.session && data.session.user);
    writeSession(s);
    if (s) sessionStorage.setItem(TAB_KEY, '1');
    return s;
  } catch (e) {
    return getSession();          // offline: keep the last known session; the API will refuse a dead token
  }
}

export async function signOut() {
  writeSession(null);
  try { await supabase.auth.signOut({ scope: 'local' }); } catch (e) { /* already gone */ }
}
// Kept for old callers: drop the local copy (the caller signs out of Supabase).
export function clearSession() { writeSession(null); }

// Tell the app when Supabase drops the session (refresh token revoked/expired).
export function onSignedOut(cb) {
  const { data } = supabase.auth.onAuthStateChange(event => {
    if (event === 'SIGNED_OUT') { writeSession(null); cb(); }
  });
  return () => { try { data.subscription.unsubscribe(); } catch (e) { /* */ } };
}

// Admin-only: create / update / remove an employee's sign-in. Runs in the staff-auth
// edge function (service role); the password goes straight into Supabase Auth and is
// never stored in the app's data.
//   manageStaffLogin({ action: 'upsert', empId, username, password?, name? })
//   manageStaffLogin({ action: 'remove', empId })
export async function manageStaffLogin(body) {
  const { data, error } = await supabase.functions.invoke('staff-auth', { body });
  if (error) {
    let msg = 'Could not update the login — check your connection and try again.';
    try { const j = await error.context.json(); if (j && j.error) msg = j.error; } catch (e) { /* not JSON */ }
    throw new Error(msg);
  }
  return data;
}

export function isAdmin()    { return getSession()?.role === 'admin'; }
export function isEmployee() { return getSession()?.role === 'employee'; }
export function getEmpId()   { return getSession()?.empId || null; }

// ── Credential generation ────────────────────────────────────────
export function generateUsername(name) {
  const parts = name.trim().toLowerCase().replace(/[^a-z\s]/g, '').split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]}.${parts[parts.length - 1]}`;
  return parts[0] + Math.floor(Math.random() * 99);
}
export function generatePassword(length = 10) {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#!';
  return Array.from(crypto.getRandomValues(new Uint8Array(length)))
    .map(b => chars[b % chars.length]).join('');
}

// ── GPS-based office location check ─────────────────────────────
// Haversine formula — returns distance in metres between two coordinates
export function gpsDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = x => x * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat/2) * Math.sin(dLat/2)
          + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2))
          * Math.sin(dLng/2) * Math.sin(dLng/2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

// Returns Promise<{ allowed, distance, accuracy, error }>
export function checkOfficeLocation() {
  return new Promise(resolve => {
    if (!navigator.geolocation) {
      resolve({ allowed: false, distance: null, error: 'Geolocation not supported by this browser.' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => {
        const dist = Math.round(gpsDistance(
          pos.coords.latitude, pos.coords.longitude,
          OFFICE_LOCATION.lat, OFFICE_LOCATION.lng
        ));
        const isMobile = /iPhone|iPad|Android|Mobile/i.test(navigator.userAgent);
        const effectiveRadius = isMobile ? OFFICE_LOCATION.radius : OFFICE_LOCATION.radius * 4;
        resolve({
          allowed:  dist <= effectiveRadius,
          distance: dist,
          radius:   effectiveRadius,
          accuracy: Math.round(pos.coords.accuracy),
          error:    null,
        });
      },
      err => {
        const msgs = {
          1: 'Location permission denied. Please allow location access to clock in.',
          2: 'Location unavailable. Please try again.',
          3: 'Location request timed out. Please try again.',
        };
        resolve({ allowed: false, distance: null, error: msgs[err.code] || 'Location error.' });
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 120000 }
    );
  });
}

// Kept as stubs so any remaining references don't crash
export async function getPublicIP() { return 'gps-mode'; }
export function isOfficeIP() { return false; }

// ── Pakistan time (PKT = UTC+5) ──────────────────────────────────
export function nowPKT() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Karachi',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(new Date());
  return parts.reduce((a, p) => { if (p.type !== 'literal') a[p.type] = p.value; return a; }, {});
}
export function nowPKTDate() { const p = nowPKT(); return `${p.year}-${p.month}-${p.day}`; }
export function nowPKTTime() { const p = nowPKT(); return `${p.hour}:${p.minute}`; }

// ── WFH schedule ─────────────────────────────────────────────────
// dow: 0=Sun 1=Mon 2=Tue 3=Wed 4=Thu 5=Fri 6=Sat
// Default schedule (fallback if not set in HR data)
const WFH_SCHEDULE_DEFAULTS = {
  emp1: [4],           // Affan Khan: Thu
  emp2: [1],           // Hamza Ashiq: Mon
  emp3: [1,2,3,4,5],   // Wajiha Amir: always
  emp4: [],            // Shoaib Ramzan: never
  emp5: [],            // Muhammad Numan: never
  emp6: [1,2,3,4,5],   // Isha Tahir: always
};

// Get permanent WFH days for an employee
// Reads from hrData.wfhDefaultDays (set via the HR UI) with fallback to hardcoded defaults
export function getDefaultWFHDays(empId, hrData) {
  const fromDB = hrData?.[empId]?.wfhDefaultDays;
  if (Array.isArray(fromDB)) return fromDB;
  return WFH_SCHEDULE_DEFAULTS[empId] || [];
}

export function isDefaultWFH(empId, date, hrData) {
  const dow = new Date(date + 'T12:00:00').getDay();
  return getDefaultWFHDays(empId, hrData).includes(dow);
}
export function isWFHDay(empId, date, hrData) {
  const overrides = hrData?.[empId]?.wfhOverrides || {};
  if (overrides[date] !== undefined) return overrides[date] === 'wfh';
  return isDefaultWFH(empId, date, hrData);
}

// ── Auto-absent check ────────────────────────────────────────────
// Returns true if now >= scheduledStart + 3 hours AND no punch-in yet
export function shouldAutoAbsent(scheduledStart, punchInTime) {
  if (punchInTime) return false;
  const p = nowPKT();
  const nowMins   = parseInt(p.hour) * 60 + parseInt(p.minute);
  const [sh, sm]  = scheduledStart.split(':').map(Number);
  return nowMins >= (sh * 60 + sm) + 180;
}

// ── Leave 12-hour rule ────────────────────────────────────────────
export function isLeaveOnTime(requestedAt, leaveDate, scheduledStart) {
  const [sh, sm] = scheduledStart.split(':').map(Number);
  // Clock-in time on leave date in UTC (PKT = UTC+5, so subtract 5h)
  const clkInUTC = new Date(`${leaveDate}T${String(sh).padStart(2,'0')}:${String(sm).padStart(2,'0')}:00+05:00`).getTime();
  return new Date(requestedAt).getTime() <= clkInUTC - 12 * 60 * 60 * 1000;
}

// ─── Silent device fingerprinting ────────────────────────────────
// Collects device info on clock-in without displaying it to the employee
export function getDeviceInfo() {
  const ua  = navigator.userAgent;
  const nav = navigator;

  // OS detection
  let os = 'Unknown';
  if (/iPhone|iPad/.test(ua))          os = /iPhone/.test(ua) ? 'iPhone' : 'iPad';
  else if (/Android/.test(ua))         os = 'Android';
  else if (/Mac OS X/.test(ua) && !/iPhone|iPad/.test(ua)) os = 'macOS';
  else if (/Windows NT/.test(ua))      os = 'Windows';
  else if (/Linux/.test(ua))           os = 'Linux';

  // Browser detection
  let browser = 'Unknown';
  if (/CriOS/.test(ua))                browser = 'Chrome (iOS)';
  else if (/FxiOS/.test(ua))          browser = 'Firefox (iOS)';
  else if (/Safari/.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';
  else if (/Chrome/.test(ua) && !/Edg/.test(ua))    browser = 'Chrome';
  else if (/Firefox/.test(ua))         browser = 'Firefox';
  else if (/Edg/.test(ua))            browser = 'Edge';

  // Device type
  let deviceType = 'Desktop';
  if (/iPhone/.test(ua))              deviceType = 'iPhone';
  else if (/iPad/.test(ua))           deviceType = 'iPad';
  else if (/Android.*Mobile/.test(ua)) deviceType = 'Android Phone';
  else if (/Android/.test(ua))        deviceType = 'Android Tablet';

  // Screen info
  const screen = `${window.screen.width}x${window.screen.height}`;
  const dpr    = window.devicePixelRatio || 1;

  // Simple fingerprint — consistent per device/browser combo
  const raw       = `${os}|${browser}|${screen}|${dpr}|${nav.language}|${nav.hardwareConcurrency || '?'}`;
  const fingerprint = raw.split('').reduce((h, ch) => ((h << 5) - h + ch.charCodeAt(0)) | 0, 0)
                        .toString(36).replace('-', 'x');

  return {
    deviceType,
    os,
    browser,
    screen,
    dpr,
    fingerprint,
    label: `${deviceType} · ${os} · ${browser}`,
  };
}
